import { API_PATHS } from "../../config";
import { apiRequest } from "./axios";
import { getShiftedDate, toLocalDateKey } from "../../utils/datetime";

/**
 * Build the shared DayDetails weather URL for an inclusive date window.
 *
 * @param {string} startKey YYYY-MM-DD
 * @param {string} endKey YYYY-MM-DD
 * @param {string} location Weather location query.
 * @returns {string}
 */
function planningWeatherRangePath(startKey, endKey, location) {
  const params = new URLSearchParams({
    start: startKey,
    end: endKey,
    location,
  });
  return `${API_PATHS.SCHEDULE.WEATHER}?${params.toString()}`;
}

/**
 * Date-keyed DayDetails weather rows for a planning date range.
 *
 * @param {string} startKey YYYY-MM-DD inclusive
 * @param {string} endKey YYYY-MM-DD inclusive
 * @param {string} [location="San Francisco"]
 * @returns {Promise<Record<string, Object>>}
 */
export async function getPlanningWeatherInRange(
  startKey,
  endKey,
  location = "San Francisco"
) {
  return apiRequest(planningWeatherRangePath(startKey, endKey, location), null, {
    method: "GET",
  });
}

/**
 * Load weather for a range as two independent requests split at "today".
 *
 * The past segment (cache-only in practice) resolves quickly and is applied
 * immediately, while the today+future segment may wait on the live forecast
 * provider. Callers receive each segment via `onSegmentLoaded` as it settles so
 * past days can paint without waiting on the slower future request.
 *
 * @param {string} startKey YYYY-MM-DD inclusive range start.
 * @param {string} endKey YYYY-MM-DD inclusive range end.
 * @param {(weatherByDate: Record<string, Object>) => void} onSegmentLoaded
 *   Called once per non-empty segment with that segment's date-keyed rows.
 * @param {string} [location="San Francisco"] Weather location query.
 * @returns {Promise<void>} Resolves after both segments settle.
 */
export async function loadPlanningWeatherSplitByDay(
  startKey,
  endKey,
  onSegmentLoaded,
  location = "San Francisco"
) {
  const todayKey = toLocalDateKey(new Date());
  const yesterdayKey = toLocalDateKey(getShiftedDate(new Date(), -1));

  // ISO date keys compare lexicographically in chronological order.
  const segments = [];
  if (startKey <= yesterdayKey) {
    segments.push([startKey, endKey < yesterdayKey ? endKey : yesterdayKey]);
  }
  if (endKey >= todayKey) {
    segments.push([startKey > todayKey ? startKey : todayKey, endKey]);
  }

  await Promise.all(
    segments.map(async ([segmentStart, segmentEnd]) => {
      const weatherByDate = await getPlanningWeatherInRange(
        segmentStart,
        segmentEnd,
        location
      );
      onSegmentLoaded(weatherByDate ?? {});
    })
  );
}

/**
 * Partial PATCH update for one planning scope block.
 *
 * @param {string|number} blockId Schedule WorkScopeAspectsBlock id.
 * @param {Object} body Partial block fields accepted by the API.
 * @returns {Promise<Object>}
 */
export async function patchPlanningScopeBlock(blockId, body) {
  return apiRequest(`${API_PATHS.SCHEDULE.BLOCKS}${blockId}/`, body, {
    method: "PATCH",
  });
}

/**
 * Bulk-update worker assignments on one or more WorkScopeAspects rows (promoted planning API).
 *
 * @param {number|Array<{scopeId: number, workerIds: number[]}>} scopeIdOrUpdates
 * @param {number[]|undefined} workerIds When first arg is a single scope id
 * @param {"dispatch"|"schedule"} planningModule Caller for auditing / dispatch_modified
 * @returns {Promise<unknown>}
 */
export async function patchScopeAspects(scopeIdOrUpdates, workerIds, planningModule) {
  const url = API_PATHS.PLANNING.PATCH_WORK_SCOPE_ASPECT_WORKERS;
  const updates = Array.isArray(scopeIdOrUpdates)
    ? scopeIdOrUpdates
    : [
        {
          scopeId: scopeIdOrUpdates,
          workerIds: Array.isArray(workerIds) ? workerIds : [],
        },
      ];
  const body = { updates, planning_module: planningModule };
  return await apiRequest(url, body, { method: "PATCH" });
}

/**
 * Partial PATCH update for one WorkScopeAspects row (promoted planning API).
 *
 * @param {number|string} scopeId
 * @param {Object|FormData} newData JSON fields or multipart FormData
 * @param {"dispatch"|"schedule"} planningModule Caller for auditing / dispatch_modified
 * @returns {Promise<unknown>}
 */
export async function updateScope(scopeId, newData, planningModule) {
  const url = API_PATHS.PLANNING.PATCH_WORK_SCOPE_ASPECT(scopeId);
  if (typeof FormData !== "undefined" && newData instanceof FormData) {
    const fd = newData;
    fd.delete("planning_module");
    fd.append("planning_module", planningModule);
    return await apiRequest(url, fd, { method: "PATCH" });
  }
  return await apiRequest(
    url,
    { ...newData, planning_module: planningModule },
    { method: "PATCH" }
  );
}

/**
 * Set ``DayDetails.locked`` for one calendar day (``PlanningDayDetailsLockView``).
 *
 * @param {string} dateMmDdYyyy Path segment ``MM-DD-YYYY`` (same convention as dispatch day-details).
 * @param {boolean} locked Whether the day is locked for planning edits.
 * @returns {Promise<unknown>}
 */
export async function setPlanningDayLocked(dateMmDdYyyy, locked) {
  const url = API_PATHS.PLANNING.DAY_DETAILS_LOCK(dateMmDdYyyy);
  return await apiRequest(url, { locked }, { method: "PATCH" });
}
